import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAuth } from 'contexts/AuthProvider';
import { selectStoreConfig } from 'containers/Layout/selectors';
import { ORDER_STAGE_TYPES, VISIBILITY_INDEX_TYPE } from 'helpers/enums';

// Views that map to a standard ecommerce funnel step. Everything else falls
// back to the generic sidebar_view screen-tracking event.
// NOTE: `purchase`/`Purchase` is intentionally NOT here — the order is reset to
// an empty cart immediately after payment succeeds, so it must be fired from
// the payment-success handler (where PAID_FOR_ORDER logs), not this hook.
const FUNNEL_EVENTS = {
  order: { ga4: 'view_cart', meta: null },
  'order/review': { ga4: 'begin_checkout', meta: 'InitiateCheckout' },
  'order/payment': { ga4: 'add_payment_info', meta: 'AddPaymentInfo' },
};

// Tier 6 — item configurator panels (rightSideViewVisibilityIndex)
const VISIBILITY_VIEWS = {
  [VISIBILITY_INDEX_TYPE.ORDER_VIEW]: 'order/item',
  [VISIBILITY_INDEX_TYPE.SPECIAL_ORDER_VIEW]: 'order/special-item',
  [VISIBILITY_INDEX_TYPE.SPECIAL_VIEW]: 'order/special',
  [VISIBILITY_INDEX_TYPE.HALF_HALF_VIEW]: 'order/half-half',
  [VISIBILITY_INDEX_TYPE.HALF_ORDER_VIEW]: 'order/half-item',
  [VISIBILITY_INDEX_TYPE.VOUCHER_VIEW]: 'order/voucher',
  [VISIBILITY_INDEX_TYPE.VOUCHER_ORDER_VIEW]: 'order/voucher-item',
};

// Tier 7 — auth sub-flow (authOrderStage)
const AUTH_VIEWS = {
  [ORDER_STAGE_TYPES.OAUTH_FORM]: 'order/auth',
  [ORDER_STAGE_TYPES.NAME_FORM]: 'order/auth/name',
  [ORDER_STAGE_TYPES.MOBILE_FORM]: 'order/auth/mobile',
  [ORDER_STAGE_TYPES.VERIFY_FROM]: 'order/auth/verify',
};

// Tier 8 — checkout stage (currentOrder.stage)
const STAGE_VIEWS = {
  [ORDER_STAGE_TYPES.REVIEW]: 'order/review',
  [ORDER_STAGE_TYPES.PAYMENT]: 'order/payment',
  [ORDER_STAGE_TYPES.PAYMENT_CARD]: 'order/payment',
};

// Builds the GA4 ecommerce payload (items + value + currency) from the order.
function buildGa4Ecommerce(currentOrder, currency) {
  const items = (currentOrder?.menuItems || []).map((m) => ({
    item_id: m._id,
    item_name: m.name,
    price: Number(m.payablePrice ?? m.itemPrice) || 0,
    quantity: Number(m.quantity) || 1,
  }));

  return {
    currency,
    value: Number(currentOrder?.payableAmount) || 0,
    items,
  };
}

// Builds the Meta Pixel payload (content_ids + value + currency) from the order.
function buildMetaEcommerce(currentOrder, currency) {
  const menuItems = currentOrder?.menuItems || [];

  return {
    currency,
    value: Number(currentOrder?.payableAmount) || 0,
    content_type: 'product',
    content_ids: menuItems.map((m) => m._id),
    contents: menuItems.map((m) => ({
      id: m._id,
      quantity: Number(m.quantity) || 1,
    })),
    num_items: menuItems.reduce((sum, m) => sum + (Number(m.quantity) || 1), 0),
  };
}

// Pushes an event into GTM's dataLayer. For ecommerce steps we first clear the
// previous `ecommerce` object (GA4 best practice) so item data doesn't bleed
// between events.
function pushToDataLayer(payload, isEcommerce) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (isEcommerce) {
    window.dataLayer.push({ ecommerce: null });
  }
  window.dataLayer.push(payload);
}

function emitSidebarAnalytics(view, currentOrder, currency) {
  if (typeof window === 'undefined') return;

  const funnel = FUNNEL_EVENTS[view];

  if (funnel) {
    const ga4Ecommerce = buildGa4Ecommerce(currentOrder, currency);
    const metaEcommerce = buildMetaEcommerce(currentOrder, currency);

    // Direct GA4
    if (typeof window.gtag === 'function') {
      window.gtag('event', funnel.ga4, ga4Ecommerce);
    }
    // Direct Meta Pixel
    if (funnel.meta && typeof window.fbq === 'function') {
      window.fbq('track', funnel.meta, metaEcommerce);
    }
    // GTM — GA4-format `ecommerce` for a GA4 tag, plus meta fields for a Pixel tag
    pushToDataLayer(
      {
        event: funnel.ga4,
        metaEvent: funnel.meta,
        ecommerce: ga4Ecommerce,
        meta: metaEcommerce,
      },
      true
    );
    return;
  }

  // Non-commerce screens — keep generic view tracking.
  const params = { view, page_location: window.location.href };

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'sidebar_view', {
      ...params,
      page_title: `sidebar:${view}`,
    });
  }

  if (typeof window.fbq === 'function') {
    window.fbq('trackCustom', 'SidebarView', params);
  }

  // GTM
  pushToDataLayer({ event: 'sidebar_view', ...params }, false);
}

/**
 * Derives the canonical view label for the current sidebar state, following the
 * exact priority order of getMainComponents() in containers/Order/index.js, and
 * fires the matching analytics event (GA4 + Meta Pixel + GTM dataLayer).
 *
 * This does NOT touch the URL — it is analytics-only.
 */
function getSidebarView({
  bookingOpen,
  aboutOpen,
  loyaltyOpen,
  profileOpen,
  visibilityIndex,
  authOrderStage,
  stage,
  hasItems,
}) {
  // Tiers 1-4: full-sidebar overlays
  if (bookingOpen) return 'booking';
  if (aboutOpen) return 'about';
  if (loyaltyOpen) return 'loyalty';
  if (profileOpen) return 'profile';

  // Tier 6: item configurator panels take priority over auth/stage
  if (visibilityIndex && VISIBILITY_VIEWS[visibilityIndex]) {
    return VISIBILITY_VIEWS[visibilityIndex];
  }

  // Tier 7: auth sub-flow
  if (AUTH_VIEWS[authOrderStage]) {
    return AUTH_VIEWS[authOrderStage];
  }

  // Order complete — sidebar is hidden, treat as home
  if (stage >= ORDER_STAGE_TYPES.ALL_DONE) {
    return 'home';
  }

  // Tier 8: checkout stage
  if (STAGE_VIEWS[stage]) {
    return STAGE_VIEWS[stage];
  }

  // Tier 5: default cart
  return hasItems ? 'order' : 'home';
}

export default function useSidebarAnalytics({ rightSideViewVisibilityIndex }) {
  const { bookingOpen, aboutOpen, loyaltyOpen, profileOpen, currentOrder } =
    useSelector((state) => state.homeReducer);
  const storeConfig = useSelector(selectStoreConfig);
  const { authOrderStage } = useAuth();

  const lastEmittedView = useRef(null);

  const stage = currentOrder?.stage ?? ORDER_STAGE_TYPES.NONE;
  const hasItems = (currentOrder?.menuItems?.length ?? 0) > 0;

  useEffect(() => {
    const view = getSidebarView({
      bookingOpen,
      aboutOpen,
      loyaltyOpen,
      profileOpen,
      visibilityIndex: rightSideViewVisibilityIndex,
      authOrderStage,
      stage,
      hasItems,
    });

    // Fire analytics once per distinct view.
    if (view !== lastEmittedView.current) {
      lastEmittedView.current = view;
      emitSidebarAnalytics(view, currentOrder, storeConfig?.currency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookingOpen,
    aboutOpen,
    loyaltyOpen,
    profileOpen,
    rightSideViewVisibilityIndex,
    authOrderStage,
    stage,
    hasItems,
  ]);
}
